import { getLanguage } from "./i18n/store.js";
import { getFeedbackPageContext } from "./feedbackContext.js";

// The web build's commit (Render provides the SHA at build; Vite inlines VITE_*
// vars). Attached to feedback so a report ties to the exact code that ran.
const RELEASE_SHA = (import.meta.env.VITE_RELEASE_SHA as string | undefined) ?? undefined;

/** Debug context sent with in-app feedback so triage doesn't need a follow-up. */
export interface FeedbackClientContext {
  releaseSha?: string;
  userAgent?: string;
  viewport?: string;
  locale?: string;
  novelTitle?: string;
  sceneId?: string;
  sceneTitle?: string;
  editorTab?: string;
}

/**
 * Snapshot the client state useful for reproducing a bug: build commit, browser,
 * viewport, and language, plus the page-published context (novel, scene, editor
 * tab) from feedbackContext. The feedback URL carries the novel *id*, but not the
 * scene the tester was on — so the player/editor pages publish that separately.
 */
export function collectClientContext(): FeedbackClientContext {
  const context: FeedbackClientContext = {};
  if (RELEASE_SHA) context.releaseSha = RELEASE_SHA;
  if (typeof navigator !== "undefined") {
    if (navigator.userAgent) context.userAgent = navigator.userAgent;
    const appLang = getLanguage();
    const browserLang = navigator.language;
    // Show the browser language only when it differs from the chosen UI language.
    context.locale = browserLang && browserLang !== appLang ? `${appLang} / ${browserLang}` : appLang;
  }
  if (typeof window !== "undefined" && window.innerWidth) {
    context.viewport = `${window.innerWidth}x${window.innerHeight}`;
  }
  const page = getFeedbackPageContext();
  if (page.novelTitle) context.novelTitle = page.novelTitle;
  if (page.sceneId) context.sceneId = page.sceneId;
  if (page.sceneTitle) context.sceneTitle = page.sceneTitle;
  if (page.editorTab) context.editorTab = page.editorTab;
  return context;
}
